// Utilitaires de calendrier mensuel partagés par l'agenda du tableau de bord
// (Dashboard.jsx > MonthAgenda), l'onglet "Prise de rdv" de la Caisse
// (pages/caisse/PriseRdvTab.jsx) et le planning du jour de l'onglet Vente
// (pages/caisse/CaissePage.jsx, modèle de vente "rendez_vous") — même façon
// de construire une grille de semaines complètes (lundi en premier) et les
// mêmes libellés, pour que les trois agendas se comportent identiquement.
import { useEffect, useState } from "react";

export const DOW_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
export const MONTH_LABELS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export function buildMonthGrid(year, month) {
  // Grille de semaines complètes (toujours des lignes de 7 jours, lundi en
  // premier) couvrant tout le mois, avec les jours des mois adjacents en
  // dehors du mois affiché (grisés) pour ne jamais casser l'alignement.
  const firstOfMonth = new Date(year, month, 1);
  const firstDow = (firstOfMonth.getDay() + 6) % 7; // 0 = lundi
  const start = new Date(year, month, 1 - firstDow);
  const weeks = [];
  let cursor = new Date(start);
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
    if (cursor.getMonth() !== month && cursor > firstOfMonth) break; // pas de 6e ligne inutile
  }
  return weeks;
}

// --- Détection de superposition de rendez-vous (onglet "Prise de rdv", voir
// PriseRdvTab.jsx) : deux rendez-vous d'un MÊME vendeur se chevauchent si
// leurs créneaux [début, début + durée] s'intersectent. Deux rendez-vous de
// vendeurs DIFFÉRENTS ne sont jamais en conflit (plusieurs vendeurs peuvent
// tout à fait recevoir des clients en même temps) — la comparaison est donc
// toujours faite à vendeur égal.

// Intervalle [début, fin] en millisecondes epoch d'un rendez-vous, à partir
// de sa date/heure et de sa durée (0 si non renseignée — traité comme un
// évènement ponctuel, qui n'entre en conflit que si un autre rendez-vous
// démarre exactement au même instant).
export function rdvInterval(rdv) {
  const start = new Date(rdv.date_heure).getTime();
  const end = start + (Number(rdv.duree_minutes) || 0) * 60000;
  return [start, end];
}

function intervalsOverlap([s1, e1], [s2, e2]) {
  if (s1 === e1 && s2 === e2) return s1 === s2; // deux évènements ponctuels : conflit seulement si identiques
  return s1 < e2 && s2 < e1;
}

// Les rendez-vous du même vendeur qui chevauchent `candidate` (un rendez-vous
// en cours de création/modification, pas encore enregistré) — `excludeId`
// exclut le rendez-vous lui-même lors d'une modification, pour qu'il ne se
// signale jamais en conflit avec sa propre version précédente.
export function findConflicts(candidate, rdvList, excludeId) {
  if (!candidate.vendeur_id || !candidate.date_heure) return [];
  const day = new Date(candidate.date_heure).toDateString();
  return (rdvList || []).filter((r) =>
    r.id !== excludeId &&
    r.vendeur_id === candidate.vendeur_id &&
    new Date(r.date_heure).toDateString() === day &&
    intervalsOverlap(rdvInterval(candidate), rdvInterval(r))
  );
}

// L'ensemble des ids de rendez-vous actuellement en conflit (superposés avec
// au moins un autre rendez-vous du même vendeur) sur toute une liste — sert à
// les afficher en violet dans le calendrier et le panneau du jour.
export function computeOverlappingIds(rdvList) {
  const overlapping = new Set();
  const byKey = {};
  for (const r of rdvList || []) {
    if (!r.vendeur_id || !r.date_heure) continue;
    const key = `${new Date(r.date_heure).toDateString()}|${r.vendeur_id}`;
    (byKey[key] = byKey[key] || []).push(r);
  }
  for (const key of Object.keys(byKey)) {
    const list = byKey[key];
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        if (intervalsOverlap(rdvInterval(list[i]), rdvInterval(list[j]))) {
          overlapping.add(list[i].id);
          overlapping.add(list[j].id);
        }
      }
    }
  }
  return overlapping;
}

// --- Statut visuel d'un rendez-vous (vert "terminé" / rouge "en retard") —
// affiché de façon identique dans les 3 agendas qui partagent cette
// collection (onglet Vente, "Prise de rdv" et tableau de bord) :
//   - "terminé" (vert) : la vente qui en découle a été clôturée à la caisse
//     (voir CaissePage.jsx > addRdvToCart, qui appelle
//     PATCH /rendez-vous/{id}/statut au moment de l'encaissement).
//   - "en retard" (rouge) : l'heure de fin prévue (date_heure + durée) est
//     dépassée et le rendez-vous n'a toujours pas été clôturé.
//   - "superposition" (violet, voir computeOverlappingIds ci-dessus) et
//     "normal" sinon.
export function isRdvTermine(rdv) {
  return rdv?.statut === "termine";
}

export function isRdvEnRetard(rdv, now = new Date()) {
  if (!rdv?.date_heure || isRdvTermine(rdv)) return false;
  const [, end] = rdvInterval(rdv);
  return now.getTime() > end;
}

// Statut agrégé (un seul à la fois, dans cet ordre de priorité) — "terminé"
// l'emporte toujours (une vente clôturée n'est plus jamais "en retard" ni
// "en superposition" à l'écran, même si elle chevauchait un autre
// rendez-vous), puis "en retard" (plus urgent qu'une simple superposition
// d'horaire), puis "superposition", puis "normal".
export function rdvVisualStatus(rdv, { overlapping = false, now = new Date() } = {}) {
  if (isRdvTermine(rdv)) return "termine";
  if (isRdvEnRetard(rdv, now)) return "en_retard";
  if (overlapping) return "superposition";
  return "normal";
}

// Horloge partagée pour recalculer "en retard" en direct (toutes les 30s par
// défaut) sans dépendre d'une action de l'utilisateur (changement de page,
// clic...) — un rendez-vous doit passer en rouge tout seul dès que l'heure
// de fin prévue est dépassée.
export function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
